import client from './client'
import type { Ticket, TicketListResponse, Intervento, TicketAllegato } from '@/types'

export interface ListaTicketParams {
  stato?: string
  cliente_id?: string
  punto_vendita_id?: string
  urgenza?: string
  canale?: string
  categoria?: string
  codice?: string
  vista?: 'attivi' | 'archivio'
  anno_chiusura?: number
  data_da?: string
  data_a?: string
  /** JSON {campo: {in|not_in: [valori]}}: vedi FiltroMulti.filtriInParametro */
  filtri?: string
  page?: number
  page_size?: number
  order_by?: string
  order_dir?: 'asc' | 'desc'
}

export interface CreaTicketPayload {
  cliente_id: string
  punto_vendita_id?: string
  impianto_id?: string
  canale: string
  tipo?: string
  categoria?: 'intervento' | 'preventivo' | 'informazione'
  urgenza?: string
  tipologia?: string
  descrizione?: string
  note?: string
}

export interface CreaInterventoFromTicketPayload {
  cliente_id?: string
  impianto_id?: string
  punto_vendita_id?: string
  accordo_quadro_id?: string
  tipologia?: 'manutenzione_ordinaria' | 'manutenzione_straordinaria'
  attivita_ids?: string[]
  descrizione?: string
  urgente?: boolean
  note_coordinatore?: string
}

export const ticketApi = {
  lista: (params: ListaTicketParams = {}) =>
    client.get<TicketListResponse>('/tickets', { params }).then(r => r.data),

  dettaglio: (id: string) =>
    client.get<Ticket>(`/tickets/${id}`).then(r => r.data),

  crea: (payload: CreaTicketPayload) =>
    client.post<Ticket>('/tickets', payload).then(r => r.data),

  aggiorna: (id: string, payload: Partial<CreaTicketPayload> & { stato?: string }) =>
    client.patch<Ticket>(`/tickets/${id}`, payload).then(r => r.data),

  annulla: (id: string) =>
    client.delete(`/tickets/${id}`),

  chiudi: (id: string) =>
    client.post(`/tickets/${id}/chiudi`).then(r => r.data),

  riapri: (id: string) =>
    client.post<Ticket>(`/tickets/${id}/riapri`).then(r => r.data),

  // Hard delete ticket + interventi collegati (solo admin)
  hardDelete: (id: string, force = false) =>
    client.delete(`/tickets/${id}/hard-delete`, { params: { force } }),

  hardDeletePreview: (id: string) =>
    client.get<{
      ticket_id: string
      codice: string
      n_interventi: number
      n_media: number
      n_preventivi: number
      n_relazioni: number
      fatture_bozza_da_eliminare: Array<{ id: string; numero_fattura: string; stato: string; interventi_del_ticket: number; interventi_totali: number; sara_eliminata: boolean }>
      fatture_emesse_bloccanti: Array<{ id: string; numero_fattura: string; stato: string; interventi_del_ticket: number; interventi_totali: number; sara_eliminata: boolean }>
      fatture_parziali_bozza: Array<{ id: string; numero_fattura: string; stato: string; interventi_del_ticket: number; interventi_totali: number; sara_eliminata: boolean }>
      fatture_parziali_emesse: Array<{ id: string; numero_fattura: string; stato: string; interventi_del_ticket: number; interventi_totali: number; sara_eliminata: boolean }>
    }>(`/tickets/${id}/hard-delete-preview`).then(r => r.data),

  listaInterventi: (ticketId: string) =>
    client.get<Intervento[]>(`/tickets/${ticketId}/interventi`).then(r => r.data),

  creaIntervento: (ticketId: string, payload: CreaInterventoFromTicketPayload) =>
    client.post<Intervento>(`/tickets/${ticketId}/interventi`, payload).then(r => r.data),

  listaNotifiche: (ticketId: string) =>
    client.get<Array<{
      id: string
      tipo: string
      canale: string
      stato: string
      destinatario: string
      oggetto: string | null
      ticket_id: string | null
      intervento_id: string | null
      inviata_il: string | null
      errore: string | null
    }>>('/notifiche', { params: { ticket_id: ticketId, limit: 100 } }).then(r => r.data),

  listaPendenti: () =>
    client.get<Ticket[]>('/ai/ticket/da-classificare-manuale').then(r => r.data),

  countPendenti: () =>
    client.get<{ count: number }>('/ai/ticket/count-pendenti').then(r => r.data),

  // Allegati (18.8)
  listaAllegati: (ticketId: string) =>
    client.get<TicketAllegato[]>(`/tickets/${ticketId}/allegati`).then(r => r.data),

  uploadAllegato: (ticketId: string, file: File, descrizione?: string) => {
    const fd = new FormData()
    fd.append('file', file)
    if (descrizione) fd.append('descrizione', descrizione)
    return client.post<TicketAllegato>(`/tickets/${ticketId}/allegati`, fd, {
      headers: { 'Content-Type': undefined },
    }).then(r => r.data)
  },

  eliminaAllegato: (allegatoId: string) =>
    client.delete(`/tickets/allegati/${allegatoId}`),
}
